import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";

import ForumIcon from "@material-ui/icons/Forum";
import SmartToyIcon from "@material-ui/icons/Memory";
import PersonIcon from "@material-ui/icons/Person";
import GroupIcon from "@material-ui/icons/Group";
import SearchIcon from "@material-ui/icons/Search";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import WarningIcon from "@material-ui/icons/Warning";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import ScheduleIcon from "@material-ui/icons/Schedule";
import SendIcon from "@material-ui/icons/Send";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    width: "100%",
    boxSizing: "border-box",
    background:
      "radial-gradient(circle at top left, rgba(37,99,235,.20), transparent 30%), radial-gradient(circle at bottom right, rgba(124,58,237,.16), transparent 30%), #020617",
    color: "#F8FAFC",
    padding: 18,
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: 10
    }
  },
  hero: {
    borderRadius: 24,
    padding: 20,
    marginBottom: 14,
    background: "linear-gradient(145deg, rgba(15,23,42,.96), rgba(2,6,23,.96))",
    border: "1px solid rgba(148,163,184,.12)",
    boxShadow: "0 22px 70px rgba(0,0,0,.34)"
  },
  heroTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "flex-start"
  },
  eyebrow: {
    color: "#60A5FA",
    fontWeight: 950,
    letterSpacing: 1.3,
    textTransform: "uppercase",
    fontSize: 11
  },
  title: {
    color: "#fff",
    fontWeight: 950,
    fontSize: 27,
    letterSpacing: -0.8,
    marginTop: 4,
    [theme.breakpoints.down("sm")]: {
      fontSize: 22
    }
  },
  subtitle: {
    color: "#94A3B8",
    fontSize: 13,
    marginTop: 4
  },
  onlineBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "rgba(34,197,94,.10)",
    border: "1px solid rgba(34,197,94,.22)",
    borderRadius: 16,
    padding: "10px 14px"
  },
  pulse: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#22C55E",
    boxShadow: "0 0 0 6px rgba(34,197,94,.13)"
  },
  metricGrid: {
    marginTop: 16
  },
  metricCard: {
    borderRadius: 18,
    padding: 16,
    minHeight: 96,
    background: "linear-gradient(145deg, rgba(255,255,255,.06), rgba(255,255,255,.025))",
    border: "1px solid rgba(148,163,184,.10)",
    position: "relative",
    overflow: "hidden",
    cursor: "pointer",
    transition: "all .22s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      borderColor: "rgba(96,165,250,.45)",
      boxShadow: "0 16px 42px rgba(37,99,235,.16)"
    }
  },
  metricGlow: {
    position: "absolute",
    width: 90,
    height: 90,
    right: -30,
    top: -30,
    borderRadius: "50%",
    opacity: .18
  },
  metricIcon: {
    width: 38,
    height: 38,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    marginBottom: 10
  },
  metricValue: {
    color: "#fff",
    fontSize: 25,
    fontWeight: 950,
    lineHeight: 1
  },
  metricLabel: {
    color: "#94A3B8",
    fontSize: 12,
    fontWeight: 800,
    marginTop: 6
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "360px minmax(0, 1fr) 330px",
    gap: 14,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "320px minmax(0, 1fr)"
    },
    [theme.breakpoints.down("sm")]: {
      display: "block"
    }
  },
  panel: {
    borderRadius: 22,
    background: "linear-gradient(145deg, rgba(15,23,42,.95), rgba(2,6,23,.93))",
    border: "1px solid rgba(148,163,184,.10)",
    boxShadow: "0 18px 55px rgba(0,0,0,.28)",
    minHeight: 560,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
      marginBottom: 12
    }
  },
  panelHeader: {
    padding: 14,
    borderBottom: "1px solid rgba(148,163,184,.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10
  },
  panelTitle: {
    color: "#F8FAFC",
    fontWeight: 950,
    fontSize: 15
  },
  searchBox: {
    margin: 14,
    height: 42,
    borderRadius: 15,
    background: "rgba(15,23,42,.92)",
    border: "1px solid rgba(148,163,184,.10)",
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    color: "#94A3B8"
  },
  searchInput: {
    flex: 1,
    background: "transparent",
    border: "none",
    outline: "none",
    color: "#E2E8F0",
    fontSize: 13,
    marginLeft: 8
  },
  tabs: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8,
    padding: "0 14px 12px"
  },
  tabBtn: {
    borderRadius: 13,
    border: "1px solid rgba(148,163,184,.10)",
    background: "rgba(255,255,255,.035)",
    color: "#94A3B8",
    padding: "9px 6px",
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer"
  },
  tabBtnActive: {
    background: "linear-gradient(135deg, rgba(37,99,235,.28), rgba(14,165,233,.14))",
    color: "#BFDBFE",
    borderColor: "rgba(96,165,250,.38)"
  },
  ticketList: {
    padding: "0 12px 14px",
    maxHeight: "calc(100vh - 330px)",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 310px)"
    }
  },
  ticketCard: {
    borderRadius: 17,
    padding: 12,
    marginBottom: 10,
    background: "rgba(255,255,255,.045)",
    border: "1px solid rgba(148,163,184,.08)",
    cursor: "pointer",
    transition: "all .18s ease",
    "&:hover": {
      background: "rgba(37,99,235,.12)",
      borderColor: "rgba(96,165,250,.32)"
    }
  },
  ticketCardActive: {
    background: "linear-gradient(135deg, rgba(37,99,235,.22), rgba(124,58,237,.12))",
    borderColor: "rgba(96,165,250,.45)"
  },
  ticketTop: {
    display: "flex",
    alignItems: "center",
    gap: 10
  },
  ticketName: {
    color: "#F8FAFC",
    fontWeight: 900,
    fontSize: 14,
    lineHeight: 1.2
  },
  ticketMsg: {
    color: "#94A3B8",
    fontSize: 12,
    marginTop: 4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  ticketMeta: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    marginTop: 10
  },
  miniChip: {
    height: 22,
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 900
  },
  chatEmpty: {
    height: 560,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    textAlign: "center",
    color: "#94A3B8",
    padding: 30
  },
  chatTop: {
    padding: 16,
    borderBottom: "1px solid rgba(148,163,184,.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12
  },
  chatContact: {
    display: "flex",
    alignItems: "center",
    gap: 12
  },
  chatBody: {
    minHeight: 390,
    padding: 18,
    background:
      "radial-gradient(circle at 20% 0%, rgba(37,99,235,.10), transparent 24%), rgba(2,6,23,.35)"
  },
  fakeBubbleLeft: {
    maxWidth: "72%",
    background: "rgba(30,41,59,.88)",
    color: "#E2E8F0",
    border: "1px solid rgba(148,163,184,.10)",
    borderRadius: "18px 18px 18px 4px",
    padding: 13,
    marginBottom: 12
  },
  fakeBubbleRight: {
    maxWidth: "72%",
    marginLeft: "auto",
    background: "linear-gradient(135deg, rgba(37,99,235,.95), rgba(14,165,233,.78))",
    color: "#fff",
    borderRadius: "18px 18px 4px 18px",
    padding: 13,
    marginBottom: 12
  },
  inputPreview: {
    borderTop: "1px solid rgba(148,163,184,.08)",
    padding: 14,
    display: "flex",
    alignItems: "center",
    gap: 10
  },
  inputFake: {
    flex: 1,
    height: 44,
    borderRadius: 16,
    background: "rgba(15,23,42,.92)",
    border: "1px solid rgba(148,163,184,.10)",
    color: "#64748B",
    display: "flex",
    alignItems: "center",
    padding: "0 14px",
    fontSize: 13
  },
  aiPanel: {
    [theme.breakpoints.down("md")]: {
      display: "none"
    }
  },
  aiCard: {
    padding: 16,
    borderBottom: "1px solid rgba(148,163,184,.08)"
  },
  aiTitle: {
    color: "#fff",
    fontWeight: 950,
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  aiText: {
    color: "#94A3B8",
    fontSize: 13,
    lineHeight: 1.55,
    marginTop: 10
  },
  scoreCircle: {
    width: 118,
    height: 118,
    borderRadius: "50%",
    margin: "16px auto",
    background: "conic-gradient(#22C55E 0deg 295deg, rgba(148,163,184,.15) 295deg 360deg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  scoreInner: {
    width: 82,
    height: 82,
    borderRadius: "50%",
    background: "#020617",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    border: "1px solid rgba(148,163,184,.10)"
  },
  actionBtn: {
    width: "100%",
    borderRadius: 13,
    textTransform: "none",
    fontWeight: 900,
    marginTop: 8,
    background: "rgba(37,99,235,.12)",
    color: "#BFDBFE",
    border: "1px solid rgba(96,165,250,.22)",
    "&:hover": {
      background: "rgba(37,99,235,.20)"
    }
  },
  mobileOnly: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex"
    }
  },
  hideMobile: {
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  }
}));

const statusLabels = {
  open: "Humanos",
  pending: "IA",
  group: "Grupos"
};

const statusColors = {
  open: "#22C55E",
  pending: "#7C3AED",
  group: "#F59E0B"
};

const TicketsV2 = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeStatus, setActiveStatus] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, pending: 0, group: 0 });
  const [searchParam, setSearchParam] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  const [loading, setLoading] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tickets", {
        params: {
          status: activeStatus,
          pageNumber: 1,
          showAll: "true",
          searchParam
        }
      });
      setTickets(data.tickets || []);
      if (!selectedTicket && data.tickets && data.tickets.length > 0 && !isMobile) {
        setSelectedTicket(data.tickets[0]);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [activeStatus, selectedTicket, isMobile, searchParam]);

  const loadCounts = useCallback(async () => {
    try {
      const statuses = ["open", "pending", "group"];
      const results = await Promise.all(
        statuses.map(status =>
          api.get("/tickets", {
            params: {
              status,
              pageNumber: 1,
              showAll: "true"
            }
          })
        )
      );

      setCounts({
        open: results[0]?.data?.count || results[0]?.data?.tickets?.length || 0,
        pending: results[1]?.data?.count || results[1]?.data?.tickets?.length || 0,
        group: results[2]?.data?.count || results[2]?.data?.tickets?.length || 0
      });
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [activeStatus, searchParam]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const metrics = {
    total: counts.open + counts.pending + counts.group,
    ia: counts.pending,
    humanos: counts.open,
    grupos: counts.group
  };

  const openTicket = ticket => {
    setSelectedTicket(ticket);
    if (isMobile) {
      setMobileView("chat");
    }
  };

  const goRealTicket = () => {
    if (selectedTicket?.uuid) {
      history.push(`/tickets/${selectedTicket.uuid}`);
    }
  };

  const formatTime = value => {
    if (!value) return "";
    const date = new Date(value);
    const now = new Date();
    const diffMin = Math.floor((now - date) / 60000);

    if (diffMin < 1) return "agora";
    if (diffMin < 60) return `há ${diffMin} min`;
    if (diffMin < 1440) return `há ${Math.floor(diffMin / 60)}h`;

    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  };

  const renderTicketCard = ticket => {
    const name = ticket.contact?.name || "Contato";
    const statusColor = statusColors[ticket.status] || "#0066FF";
    const waitingTime = formatTime(ticket.updatedAt);
    const unread = Number(ticket.unreadMessages || 0);
    return (
      <div
        key={ticket.id}
        className={`${classes.ticketCard} ${selectedTicket?.id === ticket.id ? classes.ticketCardActive : ""}`}
        onClick={() => openTicket(ticket)}
      >
        <div className={classes.ticketTop}>
          <Avatar
            src={ticket.contact?.urlPicture || ticket.contact?.profilePicUrl}
            style={{
              background: statusColor,
              fontWeight: 900,
              width: 46,
              height: 46,
              boxShadow: `0 0 0 3px ${statusColor}22, 0 12px 26px rgba(0,0,0,.28)`
            }}
          >
            {name.charAt(0)}
          </Avatar>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <Typography className={classes.ticketName}>{name}</Typography>
              <Typography style={{ color: unread > 0 ? "#22C55E" : "#64748B", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" }}>
                {waitingTime}
              </Typography>
            </div>
            <Typography className={classes.ticketMsg}>
              {ticket.lastMessage || "Sem última mensagem"}
            </Typography>
          </div>
          {unread > 0 && (
            <Chip
              label={unread}
              size="small"
              style={{ background: "#EF4444", color: "#fff", fontWeight: 900 }}
            />
          )}
        </div>
        <div className={classes.ticketMeta}>
          <Chip
            size="small"
            label={statusLabels[ticket.status] || ticket.status}
            className={classes.miniChip}
            style={{ background: `${statusColor}22`, color: statusColor }}
          />
          {ticket.queue?.name && (
            <Chip
              size="small"
              label={ticket.queue.name}
              className={classes.miniChip}
              style={{ background: "rgba(148,163,184,.10)", color: "#CBD5E1" }}
            />
          )}
          {ticket.whatsapp?.name && (
            <Chip
              size="small"
              label={ticket.whatsapp.name}
              className={classes.miniChip}
              style={{ background: "rgba(34,197,94,.10)", color: "#86EFAC" }}
            />
          )}
          {unread > 0 && (
            <Chip
              size="small"
              label="atenção"
              className={classes.miniChip}
              style={{ background: "rgba(239,68,68,.13)", color: "#FCA5A5" }}
            />
          )}
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className={classes.panel}>
      <div className={classes.panelHeader}>
        <Typography className={classes.panelTitle}>Conversas</Typography>
        <Chip size="small" label={`${tickets.length}`} style={{ background: "rgba(37,99,235,.16)", color: "#93C5FD", fontWeight: 900 }} />
      </div>

      <div className={classes.searchBox}>
        <SearchIcon fontSize="small" />
        <input
          className={classes.searchInput}
          placeholder="Buscar conversas..."
          value={searchParam}
          onChange={e => {
            setSearchParam(e.target.value);
            setSelectedTicket(null);
          }}
        />
      </div>

      <div className={classes.tabs}>
        {["open", "pending", "group"].map(status => (
          <button
            key={status}
            className={`${classes.tabBtn} ${activeStatus === status ? classes.tabBtnActive : ""}`}
            onClick={() => {
              setActiveStatus(status);
              setSelectedTicket(null);
              setMobileView("list");
            }}
          >
            {statusLabels[status]} ({counts[status] || 0})
          </button>
        ))}
      </div>

      {loading && <LinearProgress />}

      <div className={classes.ticketList}>
        {tickets.length === 0 && !loading && (
          <Typography style={{ color: "#94A3B8", fontSize: 13, padding: 20, textAlign: "center" }}>
            Nenhuma conversa encontrada.
          </Typography>
        )}
        {tickets.map(renderTicketCard)}
      </div>
    </div>
  );

  const renderChat = () => (
    <div className={classes.panel}>
      {!selectedTicket ? (
        <div className={classes.chatEmpty}>
          <SmartToyIcon style={{ fontSize: 48, color: "#2563EB", marginBottom: 12 }} />
          <Typography style={{ color: "#fff", fontWeight: 950, fontSize: 18 }}>
            Selecione uma conversa
          </Typography>
          <Typography style={{ marginTop: 6, maxWidth: 360 }}>
            Esta é a prévia da Tickets V2. Ao abrir uma conversa, você poderá entrar no ticket real.
          </Typography>
        </div>
      ) : (
        <>
          <div className={classes.chatTop}>
            <div className={classes.chatContact}>
              {isMobile && (
                <IconButton onClick={() => setMobileView("list")} style={{ color: "#CBD5E1" }}>
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Avatar src={selectedTicket.contact?.urlPicture || selectedTicket.contact?.profilePicUrl} style={{ background: statusColors[selectedTicket.status] || "#2563EB" }}>
                {(selectedTicket.contact?.name || "C").charAt(0)}
              </Avatar>
              <div>
                <Typography style={{ color: "#fff", fontWeight: 950 }}>
                  {selectedTicket.contact?.name || "Contato"}
                </Typography>
                <Typography style={{ color: "#94A3B8", fontSize: 12 }}>
                  {statusLabels[selectedTicket.status]} • {selectedTicket.queue?.name || "Sem fila"}
                </Typography>
              </div>
            </div>
            <Button className={classes.actionBtn} style={{ width: "auto", padding: "8px 14px" }} onClick={goRealTicket}>
              Abrir ticket real
            </Button>
          </div>

          <div className={classes.chatBody}>
            <div className={classes.fakeBubbleLeft}>
              <Typography style={{ fontSize: 13 }}>
                {selectedTicket.lastMessage || "Última mensagem do cliente aparecerá aqui."}
              </Typography>
            </div>
            <div className={classes.fakeBubbleRight}>
              <Typography style={{ fontSize: 13 }}>
                Prévia premium da conversa. Na próxima etapa, vamos renderizar as mensagens reais aqui.
              </Typography>
            </div>
            <div className={classes.fakeBubbleLeft}>
              <Typography style={{ fontSize: 13 }}>
                Status atual: {statusLabels[selectedTicket.status]}.
              </Typography>
            </div>
          </div>

          <div className={classes.inputPreview}>
            <div className={classes.inputFake}>Digite sua mensagem...</div>
            <IconButton style={{ background: "#2563EB", color: "#fff" }}>
              <SendIcon />
            </IconButton>
          </div>
        </>
      )}
    </div>
  );

  const renderAIPanel = () => (
    <div className={`${classes.panel} ${classes.aiPanel}`}>
      <div className={classes.aiCard}>
        <Typography className={classes.aiTitle}>
          <SmartToyIcon style={{ color: "#A78BFA" }} />
          IA Korax
        </Typography>
        <Typography className={classes.aiText}>
          Painel inteligente do atendimento. Aqui vamos trazer resumo real, sentimento, próxima ação e sugestões.
        </Typography>

        <div className={classes.scoreCircle}>
          <div className={classes.scoreInner}>
            <Typography style={{ color: "#fff", fontSize: 24, fontWeight: 950 }}>82%</Typography>
            <Typography style={{ color: "#94A3B8", fontSize: 11 }}>chance</Typography>
          </div>
        </div>

        <Button className={classes.actionBtn}>Gerar resposta</Button>
        <Button className={classes.actionBtn}>Enviar áudio IA</Button>
        <Button className={classes.actionBtn}>Criar oportunidade</Button>
        <Button className={classes.actionBtn}>Agendar reunião</Button>
      </div>

      <div className={classes.aiCard}>
        <Typography className={classes.aiTitle}>
          <TrendingUpIcon style={{ color: "#22C55E" }} />
          Próxima ação
        </Typography>
        <Typography className={classes.aiText}>
          Selecione uma conversa para a Korax sugerir o melhor próximo passo.
        </Typography>
      </div>

      <div className={classes.aiCard}>
        <Typography className={classes.aiTitle}>
          <WarningIcon style={{ color: "#F59E0B" }} />
          Risco
        </Typography>
        <Typography className={classes.aiText}>
          Nenhum alerta crítico detectado nesta prévia.
        </Typography>
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.heroTop}>
          <div>
            <Typography className={classes.eyebrow}>Korax Atendimento</Typography>
            <Typography className={classes.title}>Centro de Atendimento IA</Typography>
            <Typography className={classes.subtitle}>
              Uma nova experiência para gerenciar conversas humanas, atendimentos por IA e grupos.
            </Typography>
          </div>

          <div className={classes.onlineBox}>
            <div className={classes.pulse} />
            <div>
              <Typography style={{ fontWeight: 950, color: "#fff" }}>IA Online</Typography>
              <Typography style={{ color: "#86EFAC", fontSize: 12 }}>operando em tempo real</Typography>
            </div>
          </div>
        </div>

        <Grid container spacing={2} className={classes.metricGrid}>
          {[
            { label: "Total de conversas", value: metrics.total, icon: ForumIcon, color: "#2563EB", route: "/tickets-v2" },
            { label: "Aguardando IA", value: metrics.ia, icon: SmartToyIcon, color: "#7C3AED", status: "pending" },
            { label: "Atendimento humano", value: metrics.humanos, icon: PersonIcon, color: "#22C55E", status: "open" },
            { label: "Grupos", value: metrics.grupos, icon: GroupIcon, color: "#F59E0B", status: "group" }
          ].map((m, index) => {
            const Icon = m.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <div
                  className={classes.metricCard}
                  onClick={() => {
                    if (m.status) {
                      setActiveStatus(m.status);
                      setSelectedTicket(null);
                    }
                  }}
                >
                  <div className={classes.metricGlow} style={{ background: m.color }} />
                  <div className={classes.metricIcon} style={{ background: m.color }}>
                    <Icon />
                  </div>
                  <Typography className={classes.metricValue}>{m.value}</Typography>
                  <Typography className={classes.metricLabel}>{m.label}</Typography>
                </div>
              </Grid>
            );
          })}
        </Grid>
      </div>

      {isMobile ? (
        <>
          {mobileView === "list" && renderList()}
          {mobileView === "chat" && renderChat()}
        </>
      ) : (
        <div className={classes.layout}>
          {renderList()}
          {renderChat()}
          {renderAIPanel()}
        </div>
      )}
    </div>
  );
};

export default TicketsV2;
